// [PURPOSE]: Safely look up a value in a SPARSE class-progression table
//             (table that declares only level-change rows, leaving "no-change"
//             levels undefined). Returns the value for the highest declared
//             level ≤ targetLevel. This is the canonical D&D class-table
//             query pattern — RAW publishes class tables this way (only the
//             rows that actually CHANGE).
//
// [WHY]: Q15 Bard audit (2026-05-16) found `spellsKnown[lvl] || 0` direct-key
//        lookup in LevelUpWizard.js. At gap levels (Bard L12/L16/L19/L20),
//        the lookup returned undefined → 0 → spellsToLearn delta computed
//        from a phantom zero, asking the user to learn 16 spells at L13
//        instead of 1. The correct pattern already lives 25 lines above in
//        the cantrip section (`getCantripsForLevel`) — this helper extracts
//        and unit-tests it so the ~6 production callsites can backport
//        consistently. Affects (verified by audits Q14-Q17):
//          - Bard spellsKnown
//          - Eldritch Knight spellsKnown (1/3 caster, sparse)
//          - Ranger spellsKnown
//          - Arcane Trickster spellsKnown
//          - Sorcerer spellsKnown
//          - Warlock spellsKnown (Pact Magic)
//          - Druid wild_shape resource cap (special case — L20 unlimited)
//
// [INTEGRATION]: Called from LevelUpWizard.js (spellsKnown delta) and any
//                consumer of a sparse `levelN → value` table in
//                srdCharacterData.js. Pair with `feedback_sparse_table_direct_lookup_bug.md`.
//
// [CONTRACT]: lookupSparseTable(table, targetLevel)
//   - table: object mapping declared level (numeric string or number) → value
//   - targetLevel: integer-like character level (1-20 typically)
//   - Returns: value at the highest declared level ≤ targetLevel
//             • Preserves literal declared zeros (returns 0, not undefined)
//             • Returns undefined if no declared level ≤ targetLevel
//             • Null-safe: undefined/null/non-object table → undefined
//             • NaN targetLevel → undefined
//             • Non-numeric keys in the table are ignored

export function lookupSparseTable(table, targetLevel) {
  // Defensive: reject non-object tables and non-numeric targets early.
  if (table === null || table === undefined) return undefined;
  if (typeof table !== 'object') return undefined;
  if (typeof targetLevel !== 'number' || Number.isNaN(targetLevel)) return undefined;

  // Collect numeric keys only — string keys like 'foo' must not poison the lookup.
  // Object.keys returns strings; Number('foo') is NaN, filtered out by isFinite.
  const declaredLevels = Object.keys(table)
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => b - a); // descending — first match is the highest ≤ target.

  for (const lvl of declaredLevels) {
    if (lvl <= targetLevel) {
      // Look up via the numeric value; Object.keys returned strings but JS
      // object access coerces both directions, so table[lvl] hits the same row.
      return table[lvl];
    }
  }

  return undefined;
}
